import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, query, where,
  onSnapshot, serverTimestamp, orderBy, getDoc
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { useTrackerData } from './hooks/useTrackerData';
import {
  nextSopNumber, formatSopNumber, SOP_DEPARTMENTS, SOP_ORG_CODE
} from './services/applicationCounterService';
import LoadingSpinner from './components/LoadingSpinner';
import './SopSystemApp.css';

const VIEWS = {
  SOPS: 'sops',
  EVENTS: 'events'
};

const SOP_STATUSES = ['Draft', 'Published', 'Under Review', 'Retired'];
const EVENT_STATUSES = ['Open', 'Investigating', 'Resolved', 'Closed', 'Cancelled'];

function SopSystemApp() {
  const navigate = useNavigate();
  const { user, userProfile, authLoading, role, isAdmin } = useTrackerData();

  const [view, setView] = useState(VIEWS.SOPS);
  const [sops, setSops] = useState([]);
  const [events, setEvents] = useState([]);

  // SOP form state
  const [showSopForm, setShowSopForm] = useState(false);
  const [editingSop, setEditingSop] = useState(null);
  const [sopViewOnly, setSopViewOnly] = useState(false);
  const [sopForm, setSopForm] = useState(blankSopForm());
  const [sopVersions, setSopVersions] = useState([]); // versions of currently-editing SOP
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventViewOnly, setEventViewOnly] = useState(false);
  const [eventForm, setEventForm] = useState(blankEventForm());

  const [saving, setSaving] = useState(false);
  const [filterDept, setFilterDept] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEventType, setFilterEventType] = useState('All');

  function blankSopForm() {
    return {
      title: '',
      dept: SOP_DEPARTMENTS[0],
      purpose: '',
      scope: '',
      content: '',
      status: 'Draft'
    };
  }

  function blankEventForm() {
    return {
      type: 'violation',
      sopId: '',
      sopVersionAtEvent: null,
      title: '',
      description: '',
      occurredAt: new Date().toISOString().slice(0, 16),
      status: 'Open',
      correctiveAction: '',
      approvedBy: '' // for deviations
    };
  }

  useEffect(() => {
    document.title = 'SOP System - Wixon & Cross Desk Suite';
  }, []);

  // SOPs listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'sops'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setSops(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Events listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'sopEvents'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Load version history for editing SOP
  useEffect(() => {
    if (!editingSop || !showVersionHistory) return;
    const q = query(
      collection(db, 'sopVersions'),
      where('sopId', '==', editingSop.id),
      orderBy('version', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setSopVersions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [editingSop, showVersionHistory]);

  // ── SOP CRUD ─────────────────────────────────────────────────────────────
  const openCreateSop = () => {
    setEditingSop(null);
    setSopViewOnly(false);
    setSopForm(blankSopForm());
    setShowVersionHistory(false);
    setShowSopForm(true);
  };

  const openEditSop = (sop) => {
    const canEditThis = sop.createdBy === user.uid || isAdmin;
    setSopViewOnly(!canEditThis);
    setEditingSop(sop);
    setSopForm({
      title: sop.title || '',
      dept: sop.dept || SOP_DEPARTMENTS[0],
      purpose: sop.purpose || '',
      scope: sop.scope || '',
      content: sop.content || '',
      status: sop.status || 'Draft'
    });
    setShowVersionHistory(false);
    setShowSopForm(true);
  };

  const closeSopForm = () => {
    setShowSopForm(false);
    setEditingSop(null);
    setSopViewOnly(false);
    setSopForm(blankSopForm());
    setSopVersions([]);
    setShowVersionHistory(false);
  };

  // Two save modes: save-draft (no version bump) and publish (bump version + history snapshot).
  const handleSaveSop = async (e, publish) => {
    e.preventDefault();
    if (!sopForm.title.trim() || !sopForm.dept) {
      alert('Title and Department are required.');
      return;
    }
    setSaving(true);
    try {
      if (editingSop) {
        // Update existing SOP
        const updates = {
          title: sopForm.title.trim(),
          dept: sopForm.dept,
          purpose: sopForm.purpose,
          scope: sopForm.scope,
          content: sopForm.content,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        };

        if (publish) {
          // Bump version and snapshot the previous state.
          const newVersion = (editingSop.version || 0) + 1;
          updates.version = newVersion;
          updates.status = 'Published';
          updates.publishedAt = serverTimestamp();
          updates.publishedBy = user.email;
          updates.sopNumber = formatSopNumber({
            dept: sopForm.dept,
            number: editingSop.sequenceNumber,
            version: newVersion
          });

          await updateDoc(doc(db, 'sops', editingSop.id), updates);

          // Write a version-history entry capturing the new state.
          await addDoc(collection(db, 'sopVersions'), {
            sopId: editingSop.id,
            version: newVersion,
            sopNumber: updates.sopNumber,
            title: updates.title,
            dept: updates.dept,
            purpose: updates.purpose,
            scope: updates.scope,
            content: updates.content,
            publishedAt: serverTimestamp(),
            publishedBy: user.email
          });
        } else {
          // Save as draft — no version bump, no history entry.
          updates.status = sopForm.status === 'Published' ? 'Published' : 'Draft';
          await updateDoc(doc(db, 'sops', editingSop.id), updates);
        }
      } else {
        // Create new SOP
        const sequenceNumber = await nextSopNumber(sopForm.dept);
        const version = publish ? 1 : 0; // drafts are v0, first publish bumps to v1
        const sopNumber = formatSopNumber({
          dept: sopForm.dept, number: sequenceNumber,
          version: version || 1 // show v1 even for drafts in number; actual version field tracks state
        });

        const docRef = await addDoc(collection(db, 'sops'), {
          title: sopForm.title.trim(),
          dept: sopForm.dept,
          sequenceNumber,
          version,
          sopNumber,
          purpose: sopForm.purpose,
          scope: sopForm.scope,
          content: sopForm.content,
          status: publish ? 'Published' : 'Draft',
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          createdByEmail: user.email,
          updatedAt: serverTimestamp(),
          ...(publish ? {
            publishedAt: serverTimestamp(),
            publishedBy: user.email
          } : {})
        });

        // First-publish history entry
        if (publish) {
          await addDoc(collection(db, 'sopVersions'), {
            sopId: docRef.id,
            version: 1,
            sopNumber,
            title: sopForm.title.trim(),
            dept: sopForm.dept,
            purpose: sopForm.purpose,
            scope: sopForm.scope,
            content: sopForm.content,
            publishedAt: serverTimestamp(),
            publishedBy: user.email
          });
        }
      }

      closeSopForm();
    } catch (err) {
      console.error('Error saving SOP:', err);
      alert('Error saving SOP: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSop = async (sop) => {
    if (!window.confirm(`Delete SOP "${sop.title}"? Version history will remain.`)) return;
    try {
      await deleteDoc(doc(db, 'sops', sop.id));
    } catch (err) {
      console.error(err);
      alert('Could not delete SOP.');
    }
  };

  // ── Event CRUD ──────────────────────────────────────────────────────────
  const openCreateEvent = (type = 'violation') => {
    setEditingEvent(null);
    setEventViewOnly(false);
    setEventForm({ ...blankEventForm(), type });
    setShowEventForm(true);
  };

  const openEditEvent = (event) => {
    const canEditThis = event.createdBy === user.uid || isAdmin;
    setEventViewOnly(!canEditThis);
    setEditingEvent(event);
    setEventForm({
      type: event.type || 'violation',
      sopId: event.sopId || '',
      sopVersionAtEvent: event.sopVersionAtEvent || null,
      title: event.title || '',
      description: event.description || '',
      occurredAt: event.occurredAt
        ? toLocalDatetimeInput(event.occurredAt)
        : new Date().toISOString().slice(0, 16),
      status: event.status || 'Open',
      correctiveAction: event.correctiveAction || '',
      approvedBy: event.approvedBy || ''
    });
    setShowEventForm(true);
  };

  const closeEventForm = () => {
    setShowEventForm(false);
    setEditingEvent(null);
    setEventViewOnly(false);
    setEventForm(blankEventForm());
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim()) {
      alert('Title is required.');
      return;
    }
    setSaving(true);
    try {
      // If sopId set and changed, capture the current version at time of event
      let sopVersionAtEvent = eventForm.sopVersionAtEvent;
      if (eventForm.sopId && (!editingEvent || editingEvent.sopId !== eventForm.sopId)) {
        const sopSnap = await getDoc(doc(db, 'sops', eventForm.sopId));
        if (sopSnap.exists()) {
          sopVersionAtEvent = sopSnap.data().version || null;
        }
      }

      const data = {
        type: eventForm.type,
        sopId: eventForm.sopId || null,
        sopVersionAtEvent,
        title: eventForm.title.trim(),
        description: eventForm.description,
        occurredAt: eventForm.occurredAt ? new Date(eventForm.occurredAt) : null,
        status: eventForm.status,
        correctiveAction: eventForm.correctiveAction,
        approvedBy: eventForm.approvedBy,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      };

      if (editingEvent) {
        await updateDoc(doc(db, 'sopEvents', editingEvent.id), data);
      } else {
        await addDoc(collection(db, 'sopEvents'), {
          ...data,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          createdByEmail: user.email
        });
      }
      closeEventForm();
    } catch (err) {
      console.error('Error saving event:', err);
      alert('Error saving event: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!window.confirm(`Delete this ${event.type}?`)) return;
    try {
      await deleteDoc(doc(db, 'sopEvents', event.id));
    } catch (err) {
      console.error(err);
      alert('Could not delete event.');
    }
  };

  // ── Filters ──────────────────────────────────────────────────────────────
  const filteredSops = sops.filter(s => {
    if (filterDept !== 'All' && s.dept !== filterDept) return false;
    if (filterStatus !== 'All' && s.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hay = [s.title, s.purpose, s.scope, s.content, s.sopNumber]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const filteredEvents = events.filter(e => {
    if (filterEventType !== 'All' && e.type !== filterEventType) return false;
    if (filterStatus !== 'All' && e.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hay = [e.title, e.description, e.correctiveAction]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sopById = (id) => sops.find(s => s.id === id);

  const handleSignOut = async () => {
    try { await signOut(auth); navigate('/'); }
    catch (err) { console.error(err); }
  };

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Setting up..." />;

  return (
    <div className="sop-system-app">
      <header className="header">
        <div className="header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>🖥️ Desktop</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>📦 Dashboard</button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">📋 SOP System</span>
          </div>
          <div className="header-actions">
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge">{role}</span></span>
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      </header>

      <main className="sop-main">
        <div className="sop-view-tabs">
          <button
            className={`view-tab ${view === VIEWS.SOPS ? 'active' : ''}`}
            onClick={() => setView(VIEWS.SOPS)}
          >
            SOPs ({sops.length})
          </button>
          <button
            className={`view-tab ${view === VIEWS.EVENTS ? 'active' : ''}`}
            onClick={() => setView(VIEWS.EVENTS)}
          >
            Violations & Deviations ({events.length})
          </button>
        </div>

        {view === VIEWS.SOPS && (
          <>
            <div className="sop-toolbar">
              <h2>Standard Operating Procedures</h2>
              <button className="btn btn-primary" onClick={openCreateSop}>+ New SOP</button>
            </div>

            <div className="filter-bar">
              <input
                type="text" className="search-input" placeholder="Search SOPs..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              />
              <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="filter-select">
                <option value="All">All Departments</option>
                {SOP_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
                <option value="All">All Statuses</option>
                {SOP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="sop-grid">
              {filteredSops.length === 0 && (
                <div className="sop-empty">No SOPs match your filters.</div>
              )}
              {filteredSops.map(sop => (
                <div
                  key={sop.id}
                  className={`sop-card sop-card-clickable sop-status-${(sop.status || '').toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => openEditSop(sop)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openEditSop(sop);
                    }
                  }}
                >
                  <div className="sop-card-header">
                    <span className="sop-number">{sop.sopNumber || `${SOP_ORG_CODE}-${sop.dept}-SOP-?`}</span>
                    <span className={`sop-status-badge sop-status-badge-${(sop.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                      {sop.status}
                    </span>
                  </div>
                  <h4 className="sop-title">{sop.title}</h4>
                  {sop.purpose && (
                    <p className="sop-purpose">{sop.purpose.slice(0, 160)}{sop.purpose.length > 160 ? '…' : ''}</p>
                  )}
                  <div className="sop-meta">
                    <span>Dept: {sop.dept}</span>
                    <span>v{sop.version || 0}</span>
                  </div>
                  <div className="sop-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button onClick={(e) => { e.stopPropagation(); openEditSop(sop); }} className="btn btn-secondary btn-sm">
                      Open
                    </button>
                    {(sop.createdBy === user.uid || isAdmin) && (
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteSop(sop); }} className="btn-icon" title="Delete">🗑️</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {view === VIEWS.EVENTS && (
          <>
            <div className="sop-toolbar">
              <h2>SOP Violations & Deviations</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-primary" onClick={() => openCreateEvent('violation')}>
                  + Violation
                </button>
                <button className="btn btn-secondary" onClick={() => openCreateEvent('deviation')}>
                  + Deviation
                </button>
              </div>
            </div>

            <div className="filter-bar">
              <input
                type="text" className="search-input" placeholder="Search events..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              />
              <select value={filterEventType} onChange={e => setFilterEventType(e.target.value)} className="filter-select">
                <option value="All">All Types</option>
                <option value="violation">Violations</option>
                <option value="deviation">Deviations</option>
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
                <option value="All">All Statuses</option>
                {EVENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="event-grid">
              {filteredEvents.length === 0 && (
                <div className="sop-empty">No events match your filters.</div>
              )}
              {filteredEvents.map(ev => {
                const parentSop = ev.sopId ? sopById(ev.sopId) : null;
                return (
                  <div
                    key={ev.id}
                    className={`event-card sop-card-clickable event-type-${ev.type}`}
                    onClick={() => openEditEvent(ev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openEditEvent(ev);
                      }
                    }}
                  >
                    <div className="event-card-header">
                      <span className={`event-type-badge event-type-badge-${ev.type}`}>
                        {ev.type === 'violation' ? '⚠️ Violation' : '📝 Deviation'}
                      </span>
                      <span className="event-status-badge">{ev.status}</span>
                    </div>
                    <h4 className="event-title">{ev.title}</h4>
                    {parentSop && (
                      <div className="event-sop-ref">
                        Linked to: <strong>{parentSop.sopNumber}</strong>
                        {ev.sopVersionAtEvent && (
                          <span className="event-version-note"> (at v{ev.sopVersionAtEvent})</span>
                        )}
                      </div>
                    )}
                    {!parentSop && ev.sopId && (
                      <div className="event-sop-ref event-sop-missing">
                        Linked SOP no longer exists
                      </div>
                    )}
                    {!ev.sopId && (
                      <div className="event-sop-ref event-sop-unlinked">
                        Not yet linked to an SOP
                      </div>
                    )}
                    {ev.description && (
                      <p className="event-description">
                        {ev.description.slice(0, 160)}{ev.description.length > 160 ? '…' : ''}
                      </p>
                    )}
                    {ev.occurredAt && (
                      <div className="event-occurred">
                        Occurred: {ev.occurredAt?.toDate?.().toLocaleString() ||
                          new Date(ev.occurredAt).toLocaleString()}
                      </div>
                    )}
                    {ev.autoCreatedFromIncident && (
                      <div className="event-auto-created">
                        🔗 Auto-created from an incident
                      </div>
                    )}
                    <div className="sop-card-actions" onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => { e.stopPropagation(); openEditEvent(ev); }} className="btn btn-secondary btn-sm">
                        Open
                      </button>
                      {(ev.createdBy === user.uid || isAdmin) && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteEvent(ev); }} className="btn-icon" title="Delete">🗑️</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* SOP form modal */}
      {showSopForm && (
        <div className="modal-overlay" onClick={closeSopForm}>
          <div className="modal-content modal-xlarge" onClick={e => e.stopPropagation()}>
            <div className="sop-form-header">
              <h2>
                {!editingSop && 'New SOP'}
                {editingSop && sopViewOnly && 'View SOP (Read-Only)'}
                {editingSop && !sopViewOnly && 'Edit SOP'}
              </h2>
              {editingSop && (
                <div className="sop-form-meta">
                  <span className="sop-number-display">{editingSop.sopNumber}</span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowVersionHistory(v => !v)}
                  >
                    {showVersionHistory ? 'Hide' : 'Show'} Version History
                  </button>
                </div>
              )}
            </div>

            {sopViewOnly && (
              <p className="view-only-note">
                👁️ You don't have permission to edit. Read-only view.
              </p>
            )}

            {showVersionHistory && (
              <div className="version-history">
                <h3>Version History</h3>
                {sopVersions.length === 0 && <p className="updates-empty">No published versions yet.</p>}
                <table className="version-table">
                  <thead>
                    <tr>
                      <th>Version</th><th>SOP Number</th><th>Published</th><th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sopVersions.map(v => (
                      <tr key={v.id}>
                        <td>v{v.version}</td>
                        <td>{v.sopNumber}</td>
                        <td>{v.publishedAt?.toDate?.().toLocaleString() || '-'}</td>
                        <td>{v.publishedBy || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <form onSubmit={(e) => e.preventDefault()}>
              <fieldset disabled={sopViewOnly} className="form-fieldset">
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>SOP Title *</label>
                  <input type="text" value={sopForm.title}
                    onChange={e => setSopForm({ ...sopForm, title: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Department *</label>
                  <select value={sopForm.dept}
                    onChange={e => setSopForm({ ...sopForm, dept: e.target.value })}
                    disabled={!!editingSop} // dept locked after creation (number depends on it)
                  >
                    {SOP_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {editingSop && (
                    <small className="form-help">Department cannot change after creation</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <input type="text" value={sopForm.status} disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Purpose</label>
                  <textarea rows={2} value={sopForm.purpose}
                    onChange={e => setSopForm({ ...sopForm, purpose: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Scope</label>
                  <textarea rows={2} value={sopForm.scope}
                    onChange={e => setSopForm({ ...sopForm, scope: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Procedure / Content *</label>
                  <textarea rows={10} value={sopForm.content}
                    onChange={e => setSopForm({ ...sopForm, content: e.target.value })}
                    placeholder="Step-by-step procedure..." />
                </div>
              </div>

              {!sopViewOnly && (
                <div className="sop-publish-note">
                  <strong>Save as Draft</strong> updates the SOP without bumping the version.
                  <br />
                  <strong>Publish</strong> bumps the version (next: v{(editingSop?.version || 0) + 1})
                  and creates an immutable snapshot in the version history.
                </div>
              )}
              </fieldset>

              <div className="modal-actions">
                {!sopViewOnly && (
                  <>
                    <button type="button" className="btn btn-secondary"
                      onClick={(e) => handleSaveSop(e, false)} disabled={saving}>
                      {saving ? 'Saving...' : 'Save as Draft'}
                    </button>
                    <button type="button" className="btn btn-primary"
                      onClick={(e) => handleSaveSop(e, true)} disabled={saving}>
                      {saving ? 'Saving...' : `Publish v${(editingSop?.version || 0) + 1}`}
                    </button>
                  </>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeSopForm}>
                  {sopViewOnly ? 'Close' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event form modal */}
      {showEventForm && (
        <div className="modal-overlay" onClick={closeEventForm}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <h2>
              {!editingEvent && (eventForm.type === 'violation' ? 'New SOP Violation' : 'New SOP Deviation')}
              {editingEvent && eventViewOnly && `View ${eventForm.type === 'violation' ? 'Violation' : 'Deviation'} (Read-Only)`}
              {editingEvent && !eventViewOnly && `Edit ${eventForm.type === 'violation' ? 'Violation' : 'Deviation'}`}
            </h2>
            <p className="event-type-help">
              {eventForm.type === 'violation'
                ? 'Unplanned departure from a documented procedure. Captures what went wrong and corrective action.'
                : 'Planned, approved departure from a documented procedure. Requires approval and justification.'}
            </p>
            {eventViewOnly && (
              <p className="view-only-note">
                👁️ You don't have permission to edit. Read-only view.
              </p>
            )}
            <form onSubmit={handleSaveEvent}>
              <fieldset disabled={eventViewOnly} className="form-fieldset">
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Title *</label>
                  <input type="text" value={eventForm.title}
                    onChange={e => setEventForm({ ...eventForm, title: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Linked SOP</label>
                  <select value={eventForm.sopId}
                    onChange={e => setEventForm({ ...eventForm, sopId: e.target.value })}>
                    <option value="">-- None --</option>
                    {sops.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.sopNumber || s.title} — {s.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={eventForm.status}
                    onChange={e => setEventForm({ ...eventForm, status: e.target.value })}>
                    {EVENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Occurred At</label>
                  <input type="datetime-local" value={eventForm.occurredAt}
                    onChange={e => setEventForm({ ...eventForm, occurredAt: e.target.value })} />
                </div>
                {eventForm.type === 'deviation' && (
                  <div className="form-group">
                    <label>Approved By *</label>
                    <input type="text" value={eventForm.approvedBy}
                      onChange={e => setEventForm({ ...eventForm, approvedBy: e.target.value })}
                      placeholder="Name of approver" />
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Description *</label>
                  <textarea rows={4} value={eventForm.description}
                    onChange={e => setEventForm({ ...eventForm, description: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>{eventForm.type === 'violation' ? 'Corrective Action' : 'Justification & Plan'}</label>
                  <textarea rows={3} value={eventForm.correctiveAction}
                    onChange={e => setEventForm({ ...eventForm, correctiveAction: e.target.value })} />
                </div>
              </div>
              </fieldset>
              <div className="modal-actions">
                {!eventViewOnly && (
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingEvent ? 'Save Changes' : 'Create')}
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeEventForm}>
                  {eventViewOnly ? 'Close' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function toLocalDatetimeInput(value) {
  let d;
  if (value?.toDate) d = value.toDate();
  else if (value instanceof Date) d = value;
  else d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default SopSystemApp;
